import * as Yup from 'yup';
import dayjs from 'dayjs';
import { usPhoneValidationSchema } from '../../../components/common/Phone';
import {
  US_COUNTRY_CODE,
  US_ZIP_CODE_REGEX,
  usStateCodes,
} from '../../../constants/usStates';

export const ENTITY_TYPES = ['SITE', 'CRO', 'SITE_MEMBER', 'SUBJECT', 'CUSTOM'];

export const bankCountries = [
  { name: 'United States', code: 'US', currency: 'USD' },
  { name: 'Australia', code: 'AU', currency: 'AUD' },
  { name: 'India', code: 'IN', currency: 'INR' },
  { name: 'Japan', code: 'JP', currency: 'JPY' },
  { name: 'Canada', code: 'CA', currency: 'CAD' },
  { name: 'United Kingdom', code: 'GB', currency: 'GBP' },
  { name: 'Germany', code: 'DE', currency: 'EUR' },
  { name: 'France', code: 'FR', currency: 'EUR' },
];

const addressSchema = Yup.object().shape({
  city: Yup.string().required('City is required'),
  country: Yup.string()
    .required('Country is required')
    .oneOf([US_COUNTRY_CODE], 'Address must be in the United States'),
  line1: Yup.string().required('Address Line 1 is required'),
  line2: Yup.string(),
  postalCode: Yup.string()
    .required('Postal Code is required')
    .matches(US_ZIP_CODE_REGEX, 'Enter a valid US ZIP code (e.g. 12345 or 12345-6789)'),
  state: Yup.string()
    .required('State is required')
    .oneOf(usStateCodes, 'Please select a supported US state'),
});

export const individualSchema = Yup.object().shape({
  firstName: Yup.string().required('First Name is required'),
  lastName: Yup.string().required('Last Name is required'),
  phone: usPhoneValidationSchema.concat(Yup.string().required('Contact Number is required')),
  email: Yup.string().email('Invalid email address').required('Email is required'),
  dob: Yup.mixed()
    .nullable()
    .required('Date of Birth is required')
    .test('valid-dob', 'Date of Birth is required', (value) => {
      if (!value) return false;
      const date = dayjs.isDayjs(value) ? value : dayjs(value);
      return date.isValid() && date.isBefore(dayjs(), 'day');
    }),
  address: addressSchema,
  id_number: Yup.string()
    .transform((value) => (value ? value.replace(/\D/g, '') : ''))
    .length(9, 'Social Security Number must be 9 digits')
    .matches(/^\d{9}$/, 'Social Security Number must contain only digits')
    .required('Social Security Number is required'),
});

export const companySchema = Yup.object().shape({
  name: Yup.string().required('Business name is required'),
  taxId: Yup.string().required('Tax ID is required'),
  address: addressSchema,
});

export const bankDetailsSchema = Yup.object().shape({
  accountHolderName: Yup.string().required('Account Holder Name is required'),
  country: Yup.string()
    .required('Country is required')
    .matches(/^[A-Z]{2}$/, 'Country must be a 2-letter ISO 3166-1 alpha-2 code'),
  currency: Yup.string().required('Currency is required'),
  accountHolderType: Yup.string()
    .oneOf(['company', 'individual'], 'Account Holder Type must be Company or Individual')
    .required('Account Holder Type is required'),
  routingNumber: Yup.string()
    .required('Routing Number is required')
    .matches(/^\d{9}$/, 'Routing Number must be 9 digits'),
  accountNumber: Yup.string()
    .required('Account Number is required')
    .matches(/^\d+$/, 'Account Number must contain only digits'),
  confirmAccountNumber: Yup.string()
    .required('Confirm Account Number is required')
    .matches(/^\d+$/, 'Account Number must contain only digits')
    .test('accountNumbersMatch', 'Account Numbers must match', function (value) {
      return value === this.parent.accountNumber;
    }),
});

export const entityOnboardValidationSchema = Yup.object().shape({
  entityType: Yup.string().oneOf(ENTITY_TYPES).required('Entity type is required'),
  customEntityType: Yup.string().when('entityType', {
    is: 'CUSTOM',
    then: (schema) =>
      schema.trim().required('Custom entity type is required').max(50, 'Max 50 characters'),
    otherwise: (schema) => schema.notRequired(),
  }),
  businessType: Yup.string().oneOf(['individual', 'company']).required('Business type is required'),
  externalMasterId: Yup.string().required('External Master ID is required'),
  individual: Yup.object().when('businessType', {
    is: 'individual',
    then: () => individualSchema,
    otherwise: (schema) => schema.notRequired(),
  }),
  representative: Yup.object().when('businessType', {
    is: 'company',
    then: () => individualSchema,
    otherwise: (schema) => schema.notRequired(),
  }),
  company: Yup.object().when('businessType', {
    is: 'company',
    then: () => companySchema,
    otherwise: (schema) => schema.notRequired(),
  }),
  bankDetails: bankDetailsSchema,
});

const emptyPerson = () => ({
  firstName: '',
  lastName: '',
  phone: '',
  email: '',
  dob: null,
  address: {
    city: '',
    country: US_COUNTRY_CODE,
    line1: '',
    line2: '',
    postalCode: '',
    state: '',
  },
  id_number: '',
});

export const getInitialValues = (overrides = {}) => ({
  entityType: 'SITE',
  customEntityType: '',
  businessType: 'company',
  externalMasterId: '',
  individual: emptyPerson(),
  representative: emptyPerson(),
  company: {
    name: '',
    taxId: '',
    address: {
      city: '',
      country: US_COUNTRY_CODE,
      line1: '',
      line2: '',
      postalCode: '',
      state: '',
    },
  },
  bankDetails: {
    accountHolderName: '',
    country: US_COUNTRY_CODE,
    currency: 'USD',
    accountHolderType: 'company',
    routingNumber: '',
    accountNumber: '',
    confirmAccountNumber: '',
  },
  ...overrides,
});

const mapPersonToApi = (person) => ({
  firstName: person.firstName,
  lastName: person.lastName,
  phone: person.phone,
  email: person.email,
  dob: {
    day: person.dob.date(),
    month: person.dob.month() + 1,
    year: person.dob.year(),
  },
  address: {
    city: person.address.city,
    country: US_COUNTRY_CODE,
    line1: person.address.line1,
    line2: person.address.line2 || '',
    postal_code: person.address.postalCode,
    state: person.address.state,
  },
  id_number: person.id_number.replace(/\D/g, ''),
});

const mapAddressToApi = (address) => ({
  city: address.city,
  country: US_COUNTRY_CODE,
  line1: address.line1,
  line2: address.line2 || '',
  postal_code: address.postalCode,
  state: address.state,
});

export const mapFormValuesToPayload = (values) => {
  const personSource =
    values.businessType === 'company' ? values.representative : values.individual;
  const individual = mapPersonToApi(personSource);

  const entityType =
    values.entityType === 'CUSTOM'
      ? values.customEntityType.trim()
      : values.entityType;

  const accountHolderName =
    values.bankDetails.accountHolderName ||
    (values.businessType === 'company'
      ? values.company.name
      : `${individual.firstName} ${individual.lastName}`);

  return {
    email: individual.email,
    contact: individual.phone,
    country: values.bankDetails.country,
    businessType: values.businessType,
    entityType,
    externalMasterId: String(values.externalMasterId),
    individual,
    ...(values.businessType === 'company' && {
      company: {
        name: values.company.name,
        taxId: values.company.taxId,
        address: mapAddressToApi(values.company.address),
      },
    }),
    bankDetails: {
      country: values.bankDetails.country,
      currency: values.bankDetails.currency.toLowerCase(),
      accountHolderName,
      accountHolderType: values.bankDetails.accountHolderType,
      routingNumber: values.bankDetails.routingNumber,
      accountNumber: values.bankDetails.accountNumber,
    },
  };
};

export const getEntityTypeDefaults = (entityType) => {
  switch (entityType) {
    case 'SITE':
    case 'CRO':
      return { businessType: 'company', accountHolderType: 'company' };
    case 'SITE_MEMBER':
    case 'SUBJECT':
      return { businessType: 'individual', accountHolderType: 'individual' };
    default:
      return {};
  }
};
